import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { Club, Region } from '@court-sync/shared';
import {
  Button,
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@/components/ui';
import { clubService } from '@/services/club';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

interface ClubMembershipSettingsTabProps {
  club: Club;
  onSaved: (club: Club) => void;
}

interface MembershipSettingsFormValues {
  region: Region | '';
  stateOrProvince: string;
  membershipYearStartMonth: number;
  membershipYearStartDay: number;
}

function toFormValues(club: Club): MembershipSettingsFormValues {
  return {
    region: club.region ?? '',
    stateOrProvince: club.stateOrProvince ?? '',
    membershipYearStartMonth: club.membershipYearStartMonth,
    membershipYearStartDay: club.membershipYearStartDay,
  };
}

export function ClubMembershipSettingsTab({ club, onSaved }: ClubMembershipSettingsTabProps) {
  const { control, register, handleSubmit, reset, formState: { isSubmitting } } =
    useForm<MembershipSettingsFormValues>({ defaultValues: toFormValues(club) });

  const [onlineJoinEnabled, setOnlineJoinEnabled] = useState(club.membershipOnlineJoinEnabled);
  const [savingOnlineJoin, setSavingOnlineJoin] = useState(false);

  useEffect(() => {
    reset(toFormValues(club));
    setOnlineJoinEnabled(club.membershipOnlineJoinEnabled);
  }, [club, reset]);

  const marketConfigured = !!club.region;

  const onSubmitMarket = async (values: MembershipSettingsFormValues) => {
    const res = await clubService.updateClub(club.id, {
      region: values.region === '' ? null : values.region,
      stateOrProvince: values.stateOrProvince.trim() ? values.stateOrProvince.trim().toUpperCase() : null,
      membershipYearStartMonth: values.membershipYearStartMonth,
      membershipYearStartDay: values.membershipYearStartDay,
    });
    if (res.success && res.data) {
      toast.success('Membership settings updated');
      onSaved(res.data);
    } else {
      toast.error(res.error || 'Failed to update membership settings');
    }
  };

  const handleSaveOnlineJoin = async (value: boolean) => {
    setSavingOnlineJoin(true);
    try {
      const res = await clubService.updateClub(club.id, { membershipOnlineJoinEnabled: value });
      if (res.success && res.data) {
        setOnlineJoinEnabled(value);
        toast.success(value ? 'Online join enabled' : 'Online join disabled');
        onSaved(res.data);
      } else {
        toast.error(res.error || 'Failed to update online join setting');
      }
    } finally {
      setSavingOnlineJoin(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <form onSubmit={handleSubmit(onSubmitMarket)} className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Club market</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Determines the currency for membership plans and online join eligibility.
          </p>
        </div>

        <Field>
          <FieldLabel htmlFor="club-membership-region">Region</FieldLabel>
          <Controller
            name="region"
            control={control}
            render={({ field }) => (
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <SelectTrigger id="club-membership-region">
                  <SelectValue placeholder="Select a region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">United States (USD)</SelectItem>
                  <SelectItem value="AU">Australia (AUD)</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="club-membership-state">State / province (optional)</FieldLabel>
          <Input
            id="club-membership-state"
            placeholder="e.g. CA, NSW"
            className="uppercase"
            {...register('stateOrProvince')}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel htmlFor="club-membership-year-month">Membership year starts (month)</FieldLabel>
            <Controller
              name="membershipYearStartMonth"
              control={control}
              render={({ field }) => (
                <Select
                  value={String(field.value)}
                  onValueChange={(value) => field.onChange(Number(value))}
                >
                  <SelectTrigger id="club-membership-year-month">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_OPTIONS.map((month) => (
                      <SelectItem key={month.value} value={String(month.value)}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="club-membership-year-day">Day</FieldLabel>
            <Input
              id="club-membership-year-day"
              type="number"
              min={1}
              max={31}
              step={1}
              {...register('membershipYearStartDay', { valueAsNumber: true })}
            />
          </Field>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Season-anchored plans (e.g. an annual membership) expire the day before this date each
          year, regardless of when a member joined.
        </p>

        <Button type="submit" isLoading={isSubmitting}>
          Save membership settings
        </Button>
      </form>

      {!marketConfigured ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          Set a region above to enable online join.
        </div>
      ) : null}

      <div className="space-y-3 border-t border-gray-200 pt-5 dark:border-gray-700">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Online join</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Allow visitors to join and pay for a public membership plan online, without staff
            involvement.
          </p>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Enable online join</span>
          <Switch
            checked={onlineJoinEnabled}
            disabled={!marketConfigured || savingOnlineJoin}
            onCheckedChange={(checked) => void handleSaveOnlineJoin(checked)}
          />
        </div>
      </div>
    </div>
  );
}
